import { Component } from '@theme/component';

/**
 * dm-comparison — the "Comparison Killer" overlap choreography.
 *
 * Verified against Figma 213:731 + frame 172:92: from 750px the two cards overlap, the
 * muted "forgettable" card visible in front of a hidden keepsake. On first scroll into
 * view the muted card slides out (translateX 0 → -120%, 600ms) and the keepsake enters
 * (opacity 0 + translateY(40px) → visible, 500ms, 200ms later) while drifting to the
 * centre of the stage. Fires once.
 *
 * End state (the frames stop at the pre-animation state, so this is ours): the muted card
 * leaves the accessibility tree (inert + aria-hidden) and, once the keepsake's entrance
 * has finished, [data-settled] collapses the 500/420px stage to the keepsake's height so
 * no dead space is left behind.
 *
 * FAIL-SAFE: the overlap layout exists only under [data-armed], which only this script
 * sets, and only from 750px with motion allowed. Mobile, reduced motion, and no-JS all
 * get the static grid with both cards readable — matching the mobile frame 153:5659.
 * Leaving the eligible state (a resize below 750, or reduced motion switched on) drops
 * back to that static grid with both cards restored; it never re-arms after firing.
 */
// Tablet draws the overlap too (172:266), so the choreography arms from 750px up.
const DESKTOP = '(min-width: 750px)';
const REDUCED = '(prefers-reduced-motion: reduce)';
// Keepsake entrance: 200ms delay + 600ms drift. The timer only backs up transitionend.
const SETTLE_FALLBACK_MS = 1000;

class DmComparison extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {MediaQueryList | null} */
  #media = null;

  /** @type {MediaQueryList | null} */
  #reduced = null;

  #fired = false;

  /** @type {number} */
  #settleTimer = 0;

  #onMediaChange = () => this.#sync();

  /** @param {TransitionEvent} event */
  #onKeepsakeTransitionEnd = (event) => {
    if (event.target !== this.refs.keepsake) return;
    if (event.propertyName !== 'opacity' && event.propertyName !== 'translate') return;
    this.#settle();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#media = window.matchMedia(DESKTOP);
    this.#reduced = window.matchMedia(REDUCED);
    this.#media.addEventListener('change', this.#onMediaChange);
    this.#reduced.addEventListener('change', this.#onMediaChange);
    this.refs.keepsake?.addEventListener('transitionend', this.#onKeepsakeTransitionEnd);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#media?.removeEventListener('change', this.#onMediaChange);
    this.#reduced?.removeEventListener('change', this.#onMediaChange);
    this.refs.keepsake?.removeEventListener('transitionend', this.#onKeepsakeTransitionEnd);
    this.#observer?.disconnect();
    clearTimeout(this.#settleTimer);
  }

  #sync() {
    const eligible = Boolean(this.#media?.matches) && !this.#reduced?.matches;

    if (!eligible) {
      this.#reset();
      return;
    }

    // Once fired, a return to the eligible state keeps the static grid: the muted card
    // does not slide out a second time.
    if (this.#fired || this.dataset.armed !== undefined) return;

    // The generic reveal must not also drive these cards.
    this.refs.muted?.setAttribute('data-dm-reveal', 'shown');
    this.refs.keepsake?.setAttribute('data-dm-reveal', 'shown');

    this.dataset.armed = '';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#fire();
        }
      },
      { threshold: 0.3 }
    );
    this.#observer.observe(this);
  }

  #fire() {
    if (this.#fired) return;
    this.#fired = true;
    this.dataset.fired = '';
    this.#observer?.disconnect();
    this.#observer = null;

    const muted = this.refs.muted;
    if (muted instanceof HTMLElement) {
      muted.inert = true;
      muted.setAttribute('aria-hidden', 'true');
    }

    clearTimeout(this.#settleTimer);
    this.#settleTimer = window.setTimeout(() => this.#settle(), SETTLE_FALLBACK_MS);
  }

  #settle() {
    clearTimeout(this.#settleTimer);
    if (!this.#fired || this.dataset.armed === undefined) return;
    this.dataset.settled = '';
  }

  /** Back to the static layout: both cards visible and in the accessibility tree. */
  #reset() {
    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#settleTimer);

    delete this.dataset.armed;
    delete this.dataset.fired;
    delete this.dataset.settled;

    const muted = this.refs.muted;
    if (muted instanceof HTMLElement) {
      muted.inert = false;
      muted.removeAttribute('aria-hidden');
    }
  }
}

if (!customElements.get('dm-comparison')) {
  customElements.define('dm-comparison', DmComparison);
}
